import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { AlertTriangle, Mail, Paperclip, Pencil, Plus, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { PageLoader } from "@/components/ui/Spinner";
import { EmptyState, StatTile, TableShell, Th } from "@/features/power_projects/components/ui";
import { SignatureHint } from "@/features/leads/components/SignatureHint";
import { RefineButton, RefineStyleSelect } from "@/features/leads/components/TemplateRefine";
import { signatureLines, withPersonalSignature } from "@/features/leads/utils/signature";
import { useAuthStore } from "@/store/auth";
import { fileTooLargeMessage, findTooLargeFile, uploadErrorMessage } from "@/utils/managedFile";
import {
  CAMPAIGN_STATUS_COLORS,
  useCampaigns,
  useDeleteCampaign,
  useDeleteCampaignAttachment,
  useQueueState,
  useSaveCampaign,
  useUploadCampaignAttachment,
  type Campaign,
  type CampaignPayload,
  type RefineMode,
} from "@/features/leads/api/campaigns";

/**
 * Lista kampanii + stan kolejki.
 *
 * Kafelki na górze odpowiadają na pytanie „dlaczego to jeszcze nie wyszło",
 * które inaczej kończy się zgłoszeniem do IT: wysyłka bywa wyłączona, limit
 * dzienny bywa wyczerpany, a poza godzinami pracy kolejka po prostu stoi.
 */

// Punkt wyjścia dla nowej kampanii. Forma bezosobowa jest celowa: piszemy w
// imieniu działu i z nikim nie jesteśmy na „ty". Stopka działu zostaje w liczbie
// mnogiej tylko, gdy profil zakładającego nie ma nazwiska — `newCampaign` wstawia
// pod „Pozdrawiam" jego podpis, tak jak skrzynka przy odpowiedzi.
const EMPTY: CampaignPayload = {
  name: "",
  job_description: "",
  subject_template: "",
  body_format: "markdown",
  body_template:
    "{{zwrot}},\n\n" +
    "poszukujemy obecnie wykonawcy do następującego zadania: {{zlecenie}}.\n\n" +
    "{{personalizacja}}\n\n" +
    "Będziemy wdzięczni za informację, czy są Państwo zainteresowani, " +
    "oraz za przesłanie warunków współpracy.\n\n" +
    "Pozdrawiamy\nDział zakupów GK ELEKTIM\n",
  follow_up_template:
    "{{zwrot}},\n\n" +
    "wracamy do naszej wiadomości w sprawie: {{zlecenie}}. " +
    "Będziemy wdzięczni za krótką informację, czy temat jest dla Państwa interesujący.\n\n" +
    "Pozdrawiamy\nDział zakupów GK ELEKTIM\n",
  follow_up_days: [],
};

/**
 * Nowa kampania startuje z podpisem osoby, która ją zakłada — imię, nazwisko
 * i telefon MIĘDZY „Pozdrawiam" a linią działu, w treści i w przypomnieniu.
 * Ten sam mechanizm co w skrzynce: podpis siedzi w edytorze, człowiek go widzi
 * i może poprawić; wysyłka nic już nie dokleja, mail idzie z tym, co w szablonie.
 * Poprawianie istniejącej kampanii szablonu nie rusza — kto podpis usunął albo
 * podpisał się inaczej, zrobił to świadomie.
 */
function newCampaign(lines: string[]): CampaignPayload {
  return {
    ...EMPTY,
    body_template: withPersonalSignature(EMPTY.body_template, lines),
    follow_up_template: withPersonalSignature(EMPTY.follow_up_template, lines),
  };
}

/**
 * Szablon pisany jako zwykły tekst → HTML dla edytora wizualnego przy przełączeniu
 * formatu. Bez tego akapity zlewają się w jeden blok (ten sam most co w skrzynce).
 */
function asEditorHtml(text: string): string {
  const value = (text || "").trim();
  if (!value) return "";
  if (value.startsWith("<")) return value;
  return value
    .split(/\n{2,}/)
    .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

/** Powrót z HTML-a na zwykły tekst — formatowanie znika, tekst i placeholdery zostają. */
function htmlToPlain(html: string): string {
  const value = (html || "").trim();
  if (!value.startsWith("<")) return html;
  const doc = new DOMParser().parseFromString(value, "text/html");
  doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  const blocks = Array.from(doc.body.children).map((el) => el.textContent ?? "");
  const text = blocks.length > 0 ? blocks.join("\n\n") : (doc.body.textContent ?? "");
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

function formatKb(bytes: number | null): string {
  if (!bytes) return "";
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

export function CampaignsPage() {
  const { data: campaigns, isLoading } = useCampaigns();
  const { data: queue } = useQueueState();
  const save = useSaveCampaign();
  const remove = useDeleteCampaign();
  const uploadAttachment = useUploadCampaignAttachment();
  const removeAttachment = useDeleteCampaignAttachment();

  const [form, setForm] = useState<(CampaignPayload & { id?: string }) | null>(null);
  const [followUps, setFollowUps] = useState("5, 12");
  // Załączniki nowej kampanii czekają lokalnie do zapisu — kampania musi najpierw
  // dostać id, żeby było do czego je podpiąć.
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [error, setError] = useState("");
  // Styl redakcji AI jest jeden na całe okno: mail i przypomnienie mają brzmieć
  // jak od tego samego nadawcy.
  const [refineMode, setRefineMode] = useState<RefineMode>("standard");
  // Podpis osoby, która TERAZ zakłada kampanię — z profilu, jak w skrzynce.
  const user = useAuthStore((s) => s.user);
  const lines = useMemo(() => signatureLines(user), [user]);

  // Załączniki ISTNIEJĄCEJ kampanii bierzemy z listy zapytań, nie z kopii w
  // formularzu — po uploadzie/usunięciu odświeżają się same przez invalidację.
  const editedCampaign: Campaign | null = form?.id
    ? ((campaigns ?? []).find((row) => row.id === form.id) ?? null)
    : null;

  const setFormat = (next: CampaignPayload["body_format"]) => {
    if (!form || form.body_format === next) return;
    if (next === "html") {
      setForm({
        ...form,
        body_format: next,
        body_template: asEditorHtml(form.body_template),
        follow_up_template: asEditorHtml(form.follow_up_template),
      });
      return;
    }
    if (!confirm("Przejście na zwykły tekst usunie formatowanie (pogrubienia, linki). Kontynuować?")) {
      return;
    }
    setForm({
      ...form,
      body_format: next,
      body_template: htmlToPlain(form.body_template),
      follow_up_template: htmlToPlain(form.follow_up_template),
    });
  };

  // One file after another: with mutate() per file only the LAST call's onError
  // fires (TanStack v5), so a failure of an earlier file went unreported.
  const uploadToSaved = async (campaignId: string, files: File[]) => {
    setError("");
    const failed: string[] = [];
    for (const file of files) {
      try {
        await uploadAttachment.mutateAsync({ campaignId, file });
      } catch (err) {
        failed.push(uploadErrorMessage(err, `Nie udało się wgrać pliku „${file.name}”.`));
      }
    }
    if (failed.length) setError(failed.join(" "));
  };

  const submit = async () => {
    if (!form) return;
    if (!form.name.trim()) return setError("Nazwa kampanii jest wymagana.");
    if (!form.subject_template.trim()) return setError("Temat jest wymagany.");

    const days = followUps
      .split(",")
      .map((part) => Number(part.trim()))
      .filter((day) => Number.isFinite(day) && day > 0);
    if (days.length > 0 && !form.follow_up_template.trim()) {
      return setError("Podaj treść przypomnienia albo wyczyść listę dni.");
    }
    // Before the save — a file the registry refuses must not leave a half-done campaign.
    const tooLarge = findTooLargeFile(pendingFiles);
    if (tooLarge) return setError(fileTooLargeMessage(tooLarge));
    setError("");
    let saved: Campaign;
    try {
      saved = await save.mutateAsync({ ...form, follow_up_days: days });
    } catch {
      return setError("Nie udało się zapisać kampanii.");
    }
    // Od tej chwili kampania istnieje — ponowny „Zapisz" po błędzie załącznika ma
    // ją POPRAWIĆ, a nie założyć drugą.
    if (!form.id) setForm((prev) => (prev ? { ...prev, id: saved.id } : prev));
    for (const file of pendingFiles) {
      try {
        await uploadAttachment.mutateAsync({ campaignId: saved.id, file });
        setPendingFiles((prev) => prev.filter((pending) => pending !== file));
      } catch (err) {
        return setError(
          `Kampania zapisana, ale plik „${file.name}” się nie wgrał: ${uploadErrorMessage(err, "spróbuj ponownie.")}`,
        );
      }
    }
    setPendingFiles([]);
    setForm(null);
  };

  return (
    <>
      {queue && (
        <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <StatTile
            label="Wysyłka"
            value={queue.sending_enabled ? "włączona" : "wyłączona"}
            tone={queue.sending_enabled ? "ok" : "danger"}
            hint={queue.mailbox}
          />
          <StatTile
            label="Dziś wysłano"
            value={`${queue.sent_today} / ${queue.daily_cap}`}
            tone={queue.remaining_today === 0 ? "warn" : "default"}
            hint={`zostało ${queue.remaining_today}`}
          />
          <StatTile
            label="W kolejce"
            value={queue.queued}
            hint={queue.days_to_drain > 1 ? `~${queue.days_to_drain} dni` : "schodzi dziś"}
          />
          <StatTile
            label="Okno wysyłki"
            value={queue.within_window ? "otwarte" : "zamknięte"}
            tone={queue.within_window ? "ok" : "warn"}
            hint={queue.window}
          />
          <StatTile label="Kampanie" value={(campaigns ?? []).length} />
        </div>
      )}

      {queue && !queue.sending_enabled && (
        <p className="mb-5 flex items-start gap-2 border-l-2 border-amber-400 bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-amber-600 dark:text-amber-500">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Wysyłka jest wyłączona — kolejka się buduje, ale nic nie wychodzi. Włączy ją IT,
            kiedy skrzynka {queue.mailbox} i osobna subdomena nadawcza będą gotowe.
          </span>
        </p>
      )}

      <div className="mb-4 flex justify-end">
        <Button
          onClick={() => {
            setPendingFiles([]);
            setError("");
            setForm(newCampaign(lines));
          }}
        >
          <Plus size={14} className="mr-1.5 inline" /> Nowa kampania
        </Button>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : (campaigns ?? []).length === 0 ? (
        <div className="card">
          <EmptyState>Nie ma jeszcze żadnej kampanii.</EmptyState>
        </div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Kampania</Th>
              <Th>Status</Th>
              <Th>Firmy</Th>
              <Th>Kolejka</Th>
              <Th>Utworzona</Th>
              <Th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {(campaigns ?? []).map((row) => (
              <tr key={row.id} className="border-t border-[var(--color-border)] align-top">
                <td className="px-3 py-2.5">
                  <Link
                    to={`/leads/campaigns/${row.id}`}
                    className="text-sm font-medium text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
                  >
                    {row.name}
                  </Link>
                  {row.counts.replies > 0 && (
                    <Link to="/leads/inbox" className="ml-2 inline-block align-middle">
                      <Badge color="red">
                        {row.counts.replies}{" "}
                        {row.counts.replies === 1 ? "odpowiedź" : "odpowiedzi"}
                      </Badge>
                    </Link>
                  )}
                  <div className="mt-0.5 text-[11px] text-[var(--color-text-muted)]">
                    {row.subject_template}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <Badge color={CAMPAIGN_STATUS_COLORS[row.status]}>{row.status_label}</Badge>
                </td>
                <td className="px-3 py-2.5 font-mono">{row.counts.recipients}</td>
                <td className="px-3 py-2.5 font-mono text-[11px]">
                  <div>
                    <span className="text-[var(--color-text-muted)]">do zatwierdzenia</span>{" "}
                    {row.counts.draft}
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">w kolejce</span>{" "}
                    {row.counts.queued}
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">wysłane</span>{" "}
                    <span className="text-emerald-600">{row.counts.sent}</span>
                    {row.counts.failed > 0 && (
                      <span className="text-[var(--color-primary)]"> · błędy {row.counts.failed}</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-[11px] text-[var(--color-text-muted)]">
                  {row.created_by_name ?? "—"}
                  <div>{format(new Date(row.created_at), "dd.MM.yyyy")}</div>
                </td>
                <td className="px-3 py-2.5 text-right">
                  {/* Bez tego szablonu istniejącej kampanii nie dało się poprawić —
                      a to on decyduje o treści maili jeszcze niewysłanych. */}
                  <button
                    type="button"
                    title="Popraw treść kampanii"
                    onClick={() => {
                      setPendingFiles([]);
                      setError("");
                      setForm({
                        id: row.id,
                        name: row.name,
                        job_description: row.job_description ?? "",
                        subject_template: row.subject_template,
                        body_format: row.body_format ?? "markdown",
                        body_template: row.body_template,
                        follow_up_template: row.follow_up_template,
                        follow_up_days: row.follow_up_days,
                      });
                      setFollowUps((row.follow_up_days ?? []).join(", "));
                    }}
                    className="mr-2 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  >
                    <Pencil size={13} />
                  </button>
                  {row.counts.sent === 0 && (
                    <button
                      type="button"
                      title="Usuń kampanię"
                      onClick={() => {
                        if (!confirm("Usunąć kampanię razem z przygotowanymi mailami?")) return;
                        remove.mutate(row.id);
                      }}
                      className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}

      <Modal
        open={!!form}
        onClose={() => {
          setForm(null);
          setPendingFiles([]);
        }}
        title={form?.id ? "Popraw kampanię" : "Nowa kampania"}
        size="lg"
      >
        {form && (
          <div className="space-y-3">
            <div>
              <label className="section-label mb-1 block">Nazwa (widoczna tylko dla nas)</label>
              <input
                className="input-base"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="section-label mb-1 block">Czego dotyczy zapytanie</label>
              <input
                className="input-base"
                value={form.job_description}
                onChange={(e) => setForm({ ...form, job_description: e.target.value })}
                placeholder="obsługa geodezyjna budowy linii SN w rejonie Torunia, start II kw. 2026"
              />
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                Konkretne zadanie, pod które szukamy wykonawcy. Wchodzi do maila przez{" "}
                <code>{"{{zlecenie}}"}</code> i trafia do AI jako kontekst. „Szukamy stałych
                podwykonawców" to nie jest powód, dla którego ktoś odpisze — konkretna robota
                jest.
              </p>
            </div>
            <div>
              <label className="section-label mb-1 block">Temat maila</label>
              <input
                className="input-base"
                value={form.subject_template}
                onChange={(e) => setForm({ ...form, subject_template: e.target.value })}
                placeholder="Współpraca z GK ELEKTIM — {{firma}}"
              />
            </div>
            <div>
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <label className="section-label block">Treść</label>
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  <RefineStyleSelect value={refineMode} onChange={setRefineMode} />
                  <RefineButton
                    body={form.body_template}
                    format={form.body_format}
                    mode={refineMode}
                    onRefined={(next) => setForm({ ...form, body_template: next })}
                  />
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setFormat("markdown")}
                      className={`px-2 py-0.5 border ${
                        form.body_format !== "html"
                          ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                          : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                      }`}
                    >
                      Zwykły tekst
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormat("html")}
                      className={`px-2 py-0.5 border ${
                        form.body_format === "html"
                          ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                          : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                      }`}
                    >
                      Edytor HTML
                    </button>
                  </div>
                </div>
              </div>
              {form.body_format === "html" ? (
                <RichTextEditor
                  value={form.body_template}
                  onChange={(html) => setForm({ ...form, body_template: html })}
                  placeholder="Treść maila…"
                />
              ) : (
                <textarea
                  className="input-base min-h-[12rem] font-mono text-xs"
                  value={form.body_template}
                  onChange={(e) => setForm({ ...form, body_template: e.target.value })}
                />
              )}
              {!form.id && <SignatureHint lines={lines} whatGoesOut="maile kampanii pójdą" />}
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                Placeholdery: <code>{"{{zwrot}}"}</code> (→ „Szanowny Panie Kowalski" albo
                „Szanowni Państwo"), <code>{"{{firma}}"}</code>, <code>{"{{osoba}}"}</code>,{" "}
                <code>{"{{miasto}}"}</code>, <code>{"{{wojewodztwo}}"}</code>,{" "}
                <code>{"{{zlecenie}}"}</code>. Dodatkowo <code>{"{{personalizacja}}"}</code> —
                w to miejsce AI wpisze jeden akapit o konkretnej firmie. Resztę treści piszesz
                Ty i jest identyczna dla wszystkich.
              </p>
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                Link rezygnacji dokleja się sam na końcu, chyba że wstawisz{" "}
                <code>{"{{rezygnacja}}"}</code> we własnym miejscu.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <label className="section-label block">Treść przypomnienia</label>
                  <RefineButton
                    body={form.follow_up_template}
                    format={form.body_format}
                    mode={refineMode}
                    onRefined={(next) => setForm({ ...form, follow_up_template: next })}
                  />
                </div>
                {form.body_format === "html" ? (
                  <RichTextEditor
                    value={form.follow_up_template}
                    onChange={(html) => setForm({ ...form, follow_up_template: html })}
                    placeholder="Pusta = bez przypomnień"
                  />
                ) : (
                  <textarea
                    className="input-base min-h-[5rem] font-mono text-xs"
                    value={form.follow_up_template}
                    onChange={(e) => setForm({ ...form, follow_up_template: e.target.value })}
                    placeholder="Pusta = bez przypomnień"
                  />
                )}
              </div>
              <div>
                <label className="section-label mb-1 block">Przypomnienia po (dniach)</label>
                <input
                  className="input-base"
                  value={followUps}
                  onChange={(e) => setFollowUps(e.target.value)}
                  placeholder="5, 12"
                />
                <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                  Odpowiedź firmy zatrzymuje sekwencję automatycznie.
                </p>
              </div>
            </div>

            <div>
              <label className="section-label mb-1 block">
                <Paperclip size={12} className="mr-1 inline" />
                Załączniki
              </label>
              {editedCampaign && editedCampaign.attachments.length > 0 && (
                <ul className="mb-2 flex flex-wrap gap-2">
                  {editedCampaign.attachments.map((file) => (
                    <li
                      key={file.id}
                      className="flex items-center gap-1.5 border border-[var(--color-border)] px-2 py-1 text-xs"
                    >
                      <Paperclip size={11} className="shrink-0 text-[var(--color-text-muted)]" />
                      <span className="max-w-[200px] truncate" title={file.original_filename}>
                        {file.original_filename}
                      </span>
                      {file.size_bytes != null && (
                        <span className="text-[var(--color-text-muted)]">{formatKb(file.size_bytes)}</span>
                      )}
                      <button
                        type="button"
                        title="Usuń załącznik"
                        onClick={() => removeAttachment.mutate(file.id)}
                        className="ml-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                      >
                        <X size={11} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {form.id && editedCampaign ? (
                <FileDropZone
                  multiple
                  onSelect={(files) => void uploadToSaved(form.id!, files)}
                  disabled={uploadAttachment.isPending}
                  label="Przeciągnij pliki albo kliknij, żeby wybrać (maks. 15 MB łącznie)"
                />
              ) : (
                <FileDropZone
                  multiple
                  files={pendingFiles}
                  onFilesChange={setPendingFiles}
                  label="Przeciągnij pliki albo kliknij, żeby wybrać (wgrają się przy zapisie, maks. 15 MB łącznie)"
                />
              )}
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                Załączniki idą z <strong>każdym</strong> mailem kampanii — także z przypomnieniami.
                Ciężki mail łatwiej wpada w spam, więc lżej znaczy lepiej.
              </p>
            </div>

            {error && <p className="text-sm text-[var(--color-primary)]">{error}</p>}

            <div className="flex justify-end gap-2 border-t border-[var(--color-border)] pt-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setForm(null);
                  setPendingFiles([]);
                }}
              >
                Anuluj
              </Button>
              <Button onClick={submit} loading={save.isPending}>
                <Mail size={14} className="mr-1.5 inline" /> Zapisz kampanię
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
